import { useMemo } from 'react';

import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';

import { ENABLE_BLF_PRODUCT, ENABLE_BLR_SPECIFIC_CONTENT, ENABLE_SCORP_BUNDLE_V1 } from '#client/constants/experimentsFlags';

/**
 * Maps product service codes to their corresponding feature flag constants.
 * Products not in this map are always shown regardless of feature flag state.
 * Products in this map are only shown when their corresponding feature flag is enabled.
 *
 * To add a new feature-flagged product:
 * 1. Add the serviceCode as a key with the feature flag constant as the value
 * 2. Add the feature flag constant to experimentsFlags.js
 * 3. Update useProductCardFeatureFlags() to include the new flag
 *
 * @type {Record<string, string>}
 * @example
 * // Product with serviceCode 'business_license_filing' requires ENABLE_BLF_PRODUCT flag
 * PRODUCT_CARD_FEATURE_FLAGS.business_license_filing === ENABLE_BLF_PRODUCT
 */
export const PRODUCT_CARD_FEATURE_FLAGS = {
  business_license_filing: ENABLE_BLF_PRODUCT,
  s_corp_bundle: ENABLE_SCORP_BUNDLE_V1
};

/**
 * Hook to retrieve the feature flag values for the product cards
 * @returns {object} An object with the feature flag values for the product cards
 */
export function useProductCardFeatureFlags() {
  const enableBlfProduct = useExperimentFlag(ENABLE_BLF_PRODUCT, false);
  const enableBlrSpecificContent = useExperimentFlag(ENABLE_BLR_SPECIFIC_CONTENT, false);
  const enableSCorpBundleV1 = useExperimentFlag(ENABLE_SCORP_BUNDLE_V1, false);

  return useMemo(() => ({
    [ENABLE_BLF_PRODUCT]: enableBlfProduct,
    [ENABLE_BLR_SPECIFIC_CONTENT]: enableBlrSpecificContent,
    [ENABLE_SCORP_BUNDLE_V1]: enableSCorpBundleV1
  }), [enableBlfProduct, enableBlrSpecificContent, enableSCorpBundleV1]);
}

/**
 * Function to filter the product cards by the feature flag values
 * @param {object[]} cards - The product cards
 * @param {object} featureFlagValues - The feature flag values
 * @returns {object[]} The filtered product cards
 */
export function filterProductCardsByFeatureFlags(cards, featureFlagValues) {
  if (!cards || !Array.isArray(cards)) {
    return [];
  }

  return cards.filter((card) => {
    if (!card || !card.serviceCode) {
      return true;
    }

    const requiredFlag = PRODUCT_CARD_FEATURE_FLAGS[card.serviceCode];
    if (!requiredFlag) {
      return true;
    }

    return featureFlagValues[requiredFlag] === true;
  });
}
